import {
  animate,
  AnimationStyleMetadata,
  AnimationTriggerMetadata,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
/** collapsed state */
export const COLLAPSED = 'collapsed';
/** expanded state */
export const EXPANDED = 'expanded';
/** ignore animation state */
export const NONE = 'none';
/** Name of the animation for the vertical collapse */
export const VERTICAL_COLLAPSE_ANIMATION = 'verticalCollapse';
/** Name of the animation for the horizontal collapse */
export const HORIZONTAL_COLLAPSE_ANIMATION = 'horizontalCollapse';
export const GRID_COLLAPSE_ANIMATION = 'gridCollapse';

export function gridCollapse(expandedWidth: string, animationDuration = 400) {
  return createAnimation(
    GRID_COLLAPSE_ANIMATION,
    style({
      'grid-template-columns': '12rem 1fr',
    }),
    style({
      'grid-template-columns': `${expandedWidth} 1fr`,
    }),
    animationDuration,
  );
}
/**
 * Creates a horizontal collapse animation.
 * Use the constant HORIZONTAL_COLLAPSE_ANIMATION to refer to its name
 * @param expandedWidth Width to apply when expanded
 * @param animationDuration Duration of the animation in ms
 */
export function horizontalCollapse(
  expandedWidth: string,
  animationDuration = 400,
): AnimationTriggerMetadata {
  return createAnimation(
    HORIZONTAL_COLLAPSE_ANIMATION,
    style({
      width: '0',
    }),
    style({
      width: expandedWidth,
    }),
    animationDuration,
  );
}

/**
 * Creates a vertical collapse animation.
 * Use the constant VERTICAL_COLLAPSE_ANIMATION to refer to its name
 * @param expandedHeight Height to apply when expanded
 * @param collapsedHeight Height to apply when collapsed
 * @param animationDuration Duration of the animation in ms
 */
export function verticalCollapse(
  expandedHeight: string,
  collapsedHeight = '0',
  animationDuration = 400,
): AnimationTriggerMetadata {
  return createAnimation(
    VERTICAL_COLLAPSE_ANIMATION,
    style({
      height: collapsedHeight,
    }),
    style({
      height: expandedHeight,
    }),
    animationDuration,
  );
}

/**
 * Creates an animation that will apply a transition when the state changes from collapsed to expanded and viceversa
 * @param name Name to use for the animation to be triggered
 * @param collapsedStyle Style to apply when the element is collapsed
 * @param expandedStyle Style to apply when the element is expanded
 * @param animationDuration Duration of the animation in ms
 */
function createAnimation(
  name: string,
  collapsedStyle: AnimationStyleMetadata,
  expandedStyle: AnimationStyleMetadata,
  animationDuration = 400,
) {
  return trigger(name, [
    state(COLLAPSED, collapsedStyle),
    state(EXPANDED, expandedStyle),
    transition(
      `${COLLAPSED} <=> ${EXPANDED}`,
      animate(`${animationDuration}ms ease-in`),
    ),
  ]);
}
